import { useState, useCallback, useMemo } from "react";
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { toast } from 'sonner'; 
import {Link} from "react-router-dom";

export default function CreateFournisseurForm() {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    name: "",          
    name_personnel: "", 
    email: "",         
    phone: "",         
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    setSuccess(false); // Reset success state on new input
    setErrorMessage(""); // Clear general error message
  }, [errors]); // Dependency on errors to avoid stale closures

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Le nom du fournisseur est requis."; // French: Supplier name is required
    }
    if (!formData.name_personnel.trim()) {
      newErrors.name_personnel = "Le nom du personnel est requis."; // French: Personnel name is required
    }
    if (!formData.email.trim()) {
      newErrors.email = "L'adresse e-mail est requise."; // French: Email address is required
    } else if (!/\S+.\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Veuillez entrer une adresse e-mail valide."; // French: Please enter a valid email address
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Le numéro de téléphone est requis."; // French: Phone number is required
    } else if (!/^\+?[0-9\s-()]{7,20}$/.test(formData.phone)) { // Basic phone regex
      newErrors.phone = "Veuillez entrer un numéro de téléphone valide."; // French: Please enter a valid phone number
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  }, [formData]); // Dependency on formData to re-run validation when data changes

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    setErrorMessage(""); // Clear previous error message

    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire."); // French: Please correct errors in the form
      return; // Stop submission if validation fails
    }

    setIsLoading(true); 

    try {
      const response = await fetch(BASE_URL + "/api/fournisseurs", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json", 
        },
        body: JSON.stringify(formData), // Send form data as JSON
      });

      const result = await response.json(); // Parse the JSON response

      if (!response.ok) {
        // Handle API errors (e.g., validation errors from backend)
        if (response.status === 422 && result.errors) {
          setErrors(result.errors); // Set specific errors from backend validation
        } else {
          setErrorMessage(result.message || "Échec de la création du fournisseur. Veuillez réessayer."); // French: Failed to create supplier. Please try again.
        }
        toast.error(errorMessage || "Échec de la création."); // Toast error notification
        throw new Error("La création du fournisseur a échoué"); // Throw error to be caught by catch block
      }

      setSuccess(true); // Set success state
      toast.success("Fournisseur créé avec succès !"); // Toast success notification
      setFormData({ name: "", name_personnel: "", email: "", phone: "" }); // Reset form
      setErrors({}); // Clear errors
      setTimeout(() => setSuccess(false), 3000); // Hide success message after 3 seconds

    } catch (error) {
      console.error("Error creating fournisseur:", error);
      // Ensure error message is displayed even for unexpected network errors
      if (!errorMessage) { // Only set if no specific API error message was already set
        setErrorMessage("Une erreur inattendue est survenue. Veuillez réessayer."); // French: An unexpected error occurred. Please try again.
      }
      toast.error("Une erreur inattendue est survenue."); // Toast for unexpected errors
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  const previewData = useMemo(() => {
    return {
      displayName: formData.name || "Nom du Fournisseur", // Default text
      displayPersonnel: formData.name_personnel || "Personne de Contact",
      displayEmail: formData.email || "Email",
      displayPhone: formData.phone || "Téléphone",
    };
  }, [formData]); // Recompute preview data when formData changes

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-lg font-semibold">Fournisseur créé avec succès !</span>
          </div>
          <p className="text-[var(--color-primary-50)] text-xs">Votre nouveau fournisseur a été ajouté au système.</p>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-500 text-xs text-white p-4 text-center">
          <p>{errorMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        {/* Fournisseur Name */}
        <div className="mb-8">
          <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-3">Nom du Fournisseur *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Entrez le nom du fournisseur..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.name
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.name && <p className="mt-2 text-red-600 text-xs">{errors.name}</p>}
        </div>

        {/* Contact Person Name */}
        <div className="mb-8">
          <label htmlFor="name_personnel" className="block text-sm font-semibold text-gray-900 mb-3">Nom du Contact *</label>
          <input
            type="text"
            id="name_personnel"
            name="name_personnel"
            value={formData.name_personnel}
            onChange={handleInputChange}
            placeholder="Entrez le nom de la personne de contact..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.name_personnel
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.name_personnel && <p className="mt-2 text-red-600 text-xs">{errors.name_personnel}</p>}
        </div>

        {/* Email */}
        <div className="mb-8">
          <label htmlFor="email" className="block font-semibold text-sm text-gray-900 mb-3">Email *</label>
          <input
            type="email" // Use type="email" for better mobile keyboard and basic validation
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Entrez l'adresse e-mail..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.email
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.email && <p className="mt-2 text-red-600 text-xs">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div className="mb-8">
          <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-3">Téléphone *</label>
          <input
            type="tel" // Use type="tel" for better mobile keyboard
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Entrez le numéro de téléphone..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.phone
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.phone && <p className="mt-2 text-red-600 text-xs">{errors.phone}</p>}
        </div>

        {/* Actions */}
        <div className="flex sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <Link to="/fournisseurs" className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className='cursor-pointer'
            >
              Annuler  
            </button>
          </Link>  
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 cursor-pointer px-4 text-sm font-semibold rounded-2xl ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Enregistrement...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Enregistrer
                <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
            )}
          </button>
        </div>

        {/* Preview */}
        {(formData.name || formData.name_personnel || formData.email || formData.phone) && (
          <div className="mt-8 p-6 bg-gray-50 rounded-2xl">
            <h3 className="font-semibold text-gray-900 mb-4">Aperçu</h3>
            <div className="flex items-start gap-4"> {/* Changed to items-start for better alignment of multi-line text */}
              <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="h-6 w-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2m7-12a4 4 0 100-8 4 4 0 000 8zm7 0a4 4 0 100-8 4 4 0 000 8zm-7 0a4 4 0 100-8 4 4 0 000 8z"
                  />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-sm text-gray-900">{previewData.displayName}</div>
                <div className="text-xs text-gray-600 mt-1">
                  Contact: {previewData.displayPersonnel}
                </div>
                <div className="text-xs text-gray-600">
                  Email: {previewData.displayEmail}
                </div>
                <div className="text-xs text-gray-600">
                  Téléphone: {previewData.displayPhone}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
